import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { AuthError } from '../../types/auth';
import { handleAuthError } from '../../utils/auth';
import { useAnalyticsEvents } from '../../hooks/useAnalytics';

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { trackFormEvent } = useAnalyticsEvents();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await signUp(email, password);
      
      trackFormEvent('register', true);
      navigate('/dashboard');
    } catch (err) {
      const message = handleAuthError(err as AuthError);
      setError(message);
      trackFormEvent('register', false, message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-start">
          <span className="flex-shrink-0 mr-2">⚠️</span>
          {error}
        </div>
      )}
      <div className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <div className="relative">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              className="input pl-10 w-full"
              required
            />
            {!emailFocused && (
              <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-opacity" />
            )}
          </div>
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Mot de passe
          </label>
          <div className="relative">
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              className="input pl-10 w-full"
              required
              minLength={6}
            />
            {!passwordFocused && (
              <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-opacity" />
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Minimum 6 caractères
          </p>
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="btn btn-primary w-full flex items-center justify-center"
      >
        {loading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
        ) : (
          <>
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            Créer mon compte
          </>
        )}
      </button>
    </form>
  );
};

export default RegisterForm;